import { SettingsService, StandardEntity } from '@infodata-group/inf-commons';
import { GmapsPolyline } from '@infodata-group/inf-commons-tools';
import dayjs from 'dayjs';
import { AoSharedTools } from '../../engine/tools/ao-shared-tools';
import { PathTypeEnum } from '../../enum/path/path-type.enum';
import { PreferencesSettingsKeyEnum } from '../../enum/preferences-settings-key.enum';
import { PathMarker } from './path-marker/path-marker';
import { PathPoint } from './path-point/path-point';

import SymbolPath = google.maps.SymbolPath;

export abstract class AoPath extends StandardEntity {
  public date: Date;
  public pathPoint: PathPoint[] = [];
  public markers: PathMarker[] = [];
  public polyline: GmapsPolyline[] = [];

  public displayedMarkers: PathMarker[] = [];
  public displayedPolyline: GmapsPolyline[] = [];
  private _currentIndex = 0;

  public set currentIndex(index: number) {
    this._currentIndex = index;
    if (index === 0 || index === this.polyline.length - 1) {
      this.displayedPolyline = this.polyline;
      this.displayedMarkers = this.markers;
      return;
    }
    this.displayedPolyline = [];
    this.displayedMarkers = [];
    for (let i = 0; i < index; i++) {
      this.displayedPolyline.push(this.polyline[i]);
      this.polyline[i].points.forEach((p) => {
        const marker = this.markers.find(
          (m) =>
            m.latLng.lat() === p.latLng.lat() &&
            m.latLng.lng() === p.latLng.lng() &&
            !this.displayedMarkers.find((dm) => dm.id === m.id),
        );
        if (marker) {
          this.displayedMarkers.push(marker);
        }
      });
    }
    const lastDisplayedPoint = this.displayedPolyline.at(-1).getLatLng().at(-1);
    this.displayedMarkers.push(
      new PathMarker(
        'NOW',
        this.pathPoint.find(
          (p) =>
            p.latitude === lastDisplayedPoint.lat() &&
            p.longitude === lastDisplayedPoint.lng(),
        ),
      ),
    );
  }

  constructor(id: string, date: Date, pathPoint: PathPoint[]) {
    super(id);
    this.date = date;
    this.pathPoint = pathPoint;
    this._applyDateToPathPoint();
  }

  public generateMarkers(): void {
    const pathMarkers: PathMarker[] = [];
    this.pathPoint
      .filter(
        (p) =>
          p.type === PathTypeEnum.START ||
          p.type === PathTypeEnum.STOP ||
          p.type === PathTypeEnum.PAUSE,
      )
      .forEach((p) => {
        const pathMarker = this.generatePathMarker(p);
        if (p.type === PathTypeEnum.START) {
          pathMarker.type =
            pathMarkers.length === 0 ? PathTypeEnum.START : PathTypeEnum.PAUSE;
        }
        if (p.type === PathTypeEnum.STOP) {
          pathMarker.type = p.type;
        }

        if (
          pathMarker.type === PathTypeEnum.START ||
          pathMarker.type === PathTypeEnum.STOP
        ) {
          pathMarkers.push(pathMarker);
          return;
        }

        if (pathMarker.type === PathTypeEnum.PAUSE) {
          const index = pathMarkers.findIndex(
            (pm) =>
              pm.latLng.lat() === pathMarker.latLng.lat() &&
              pm.latLng.lng() === pathMarker.latLng.lng(),
          );
          if (index > -1) {
            pathMarkers.splice(index, 1, pathMarker);
            return;
          }
        }
        pathMarkers.push(pathMarker);
      });
    pathMarkers.forEach((pm) => {
      pm.setIcon(`assets/img/google-map/markers/${pm.type.toLowerCase()}.svg`);
    });

    this.markers = this.displayedMarkers = pathMarkers;
  }

  public generatePolyline(settingsService: SettingsService) {
    const colors = AoSharedTools.getColorPath(
      settingsService.getSetting(PreferencesSettingsKeyEnum.speedAndColor),
    );
    const polylines: GmapsPolyline[] = [];

    let polyline = new GmapsPolyline('', '', []);
    let previousInterval: string;
    let number = 0;
    polyline.setIcon(SymbolPath.FORWARD_CLOSED_ARROW);
    this.pathPoint.forEach((p, index) => {
      let color = '#000000';
      let interval: string;
      const matchColor = colors.find(
        (c) => c.speedMin <= p.vitesse && p.vitesse <= c.speedMax,
      );
      if (matchColor) {
        color = matchColor.color;
        interval = JSON.stringify(matchColor);
      }
      if (
        (interval === previousInterval && index > 0) ||
        number > 5 ||
        this.pathPoint.length === 5
      ) {
        polylines.push(polyline);
        polyline = new GmapsPolyline('', '', [
          this.pathPoint[index - 1].toGmapsPoint(),
        ]);
        polyline.options = { strokeWeight: 1.75 };
        polyline.setIcon(SymbolPath.FORWARD_CLOSED_ARROW);
        previousInterval = interval;
        number = 0;
      }
      polyline.setColor(color);
      polyline.points.push(p.toGmapsPoint());
      number++;
    });
    polylines.push(polyline);
    this.polyline = this.displayedPolyline = polylines;
  }

  protected _applyDateToPathPoint(): void {
    this.pathPoint.forEach((p) => {
      p.frontDate = dayjs(
        `${dayjs(this.date).format('DD/MM/YYYY')} ${p.heureDebut}`,
        'DD/MM/YYYY HH:mm:ss',
      ).toDate();
    });
    this.pathPoint.sort(
      (a, b) => a.frontDate.getTime() - b.frontDate.getTime(),
    );
  }

  public getFitBounds(): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    this.pathPoint.forEach((p) =>
      bounds.extend(new google.maps.LatLng(p.latitude, p.longitude)),
    );
    return bounds;
  }

  public abstract generatePathMarker(pathPoint: PathPoint): PathMarker;
}
